import classNames from 'classnames'
import { type FC, useMemo } from 'react'
import { AttachmentLoader } from 'shared/ui/Loader'
import { getAriaLabel } from 'shared/lib'
import { AudioPlayer } from 'shared/ui/AudioPlayer'
import { VideoCard } from 'entities/Attachment/ui/VideoCard/VideoCard'
import { ImageCard } from 'entities/Attachment/ui/ImageCard/ImageCard'
import { VCardCard } from 'entities/VCard'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { sentEventSentry } from 'entities/Sentry'
import styles from './styles.module.scss'
import { RemovableAttachment } from './RemovableAttachment/RemovableAttachment'
import { AttachmentCardContent } from './AttachmentCardContent/AttachmentCardContent'

type IAttachmentCardProps = {
  attachment?: Attachment
  file?: File
  loading?: boolean
  onRemoveAttachment?: () => void
  onCardClick?: () => void
  isGray?: boolean
  noSource?: boolean
  medium?: boolean
  fromAttachMedia?: boolean
  hideVideo?: boolean
  fullWidth?: boolean
  isConversationsHistoryView?: boolean
  imgFitType?: 'cover' | 'contain'
  isNoteMode?: boolean
  isDetailsMode?: boolean
  onLoad?: () => void
}

export const AttachmentCard: FC<IAttachmentCardProps> = ({
  attachment,
  onCardClick,
  isGray,
  file,
  loading,
  onRemoveAttachment,
  fromAttachMedia,
  medium,
  hideVideo,
  fullWidth,
  isConversationsHistoryView,
  imgFitType,
  isNoteMode,
  isDetailsMode,
  onLoad,
}) => {
  const isAudio = attachment?.type === 'audio' || file?.type.includes('audio')
  const isVideo = attachment?.type === 'video' || file?.type.includes('video')
  const isImage =
    attachment?.type === 'image' ||
    attachment?.type === 'gif' ||
    attachment?.type === 'previewGif' ||
    file?.type.includes('image')

  const handleCardClick = () => {
    if (attachment?.type === 'attachment') {
      const link = attachment?.source_short || attachment?.source

      if (link) {
        const a = document.createElement('a')

        a.href = link
        a.target = '_blank'
        a.click()
      }
    } else {
      if (isAudio) return

      onCardClick?.()
    }
  }

  const mediaContent = useMemo(() => {
    const url = attachment?.source || (file && URL.createObjectURL(file))
    const duration = attachment?.duration

    if (attachment?.type === 'vcard' && !!attachment?.vCard) {
      return <VCardCard vCard={attachment.vCard} isGray={isGray} />
    }

    if (isImage) {
      return (
        <ImageCard
          medium={medium}
          fullWidth={fullWidth}
          loading={loading}
          file={file}
          attachment={attachment}
          imgFitType={imgFitType}
          onCardClick={onCardClick}
          onLoad={onLoad}
        />
      )
    }

    if (isVideo) {
      return (
        <VideoCard
          medium={medium}
          fullWidth={fullWidth}
          loading={loading}
          file={file}
          attachment={attachment}
          onCardClick={onCardClick}
          hideVideo={hideVideo}
          onLoad={onLoad}
        />
      )
    }

    if (!fromAttachMedia && isAudio) {
      return (
        <>
          <AttachmentCardContent attachment={attachment} file={file} fromAudioCard />

          <div className={styles.wrapPlayer}>
            <AudioPlayer
              url={url}
              duration={duration}
              fromAttachMedia={fromAttachMedia}
              fileName={attachment?.name || file?.name}
              hideTranscriptionButton={isConversationsHistoryView}
              onError={(error) => {
                sentEventSentry({
                  event: '[Audio] AttachmentCard - error',
                  name: 'audio:error',
                  level: 'error',
                  error: error,
                })
              }}
            />
          </div>
        </>
      )
    }

    return (
      <button className={styles.card} onClick={handleCardClick} data-name={'attachmentCard'}>
        {isAudio && fromAttachMedia ? (
          <AudioPlayer
            url={url}
            duration={duration}
            fromAttachMedia={fromAttachMedia}
            widthWave={118}
            hideTranscriptionButton={isConversationsHistoryView}
            onError={(error) => {
              sentEventSentry({
                event: '[Audio] AttachmentCard - error',
                name: 'audio:error',
                level: 'error',
                error: error,
              })
            }}
          />
        ) : (
          <AttachmentCardContent attachment={attachment} file={file} />
        )}
        {loading && <AttachmentLoader />}
      </button>
    )
  }, [loading, attachment, file, isGray, fromAttachMedia, isAudio])

  return (
    <div
      className={classNames(styles.commonWrap, {
        [styles.audio]: isAudio && !fromAttachMedia,
        [styles.attachment]: fromAttachMedia && attachment?.type === 'attachment',
        [styles.vcard]: fromAttachMedia && attachment?.type === 'vcard',
        [styles.videoAttachment]: attachment?.type === 'video',
        [styles.conversationsHistoryView]: isConversationsHistoryView,
        [styles.noteMode]: isNoteMode,
        [styles.detailsMode]: isDetailsMode,
      })}
      aria-label={getAriaLabel('AttachmentCard')}
    >
      {onRemoveAttachment ? (
        <RemovableAttachment
          onRemove={onRemoveAttachment}
          isGray={isImage || isVideo}
          isNoteMode={isNoteMode}
        >
          {mediaContent}
        </RemovableAttachment>
      ) : (
        mediaContent
      )}
    </div>
  )
}
